// Question Bank API Service Layer
import CONF from '@/Config.js'
import axios from 'axios'
import { normalizeGroupVideos } from '@/utils/groupVideos'

// 직접 API 호출을 처리하는 서비스 클래스
class QuestionBankService {
  
  // API 호출 헬퍼 함수
  async makeRequest(config) {
    const token   = localStorage.getItem("access_token");
    const apiUrl  = config.api_url || CONF.ACHIEVE_API_URL;
    const fullUrl = apiUrl + config.url;
    
    //console.log('API Request - fullUrl:', fullUrl);
    const response = await axios({
      method: config.method,
      url: fullUrl,
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
        ...config.headers
      },
      data: config.data,
      params: config.params
    });

    return response;
  }

  // MongoDB _id를 groupId로 매핑하는 헬퍼 함수
  mapGroupData(group) {
    if (!group) return group;
    //console.log('mapGroupData input:', group);
    
    // groupId 우선순위: 기존 groupId > _id > 임시 ID 생성
    const groupId = group.groupId || group._id || group.id || `temp-${Date.now()}-${Math.random()}`;
    
    const mapped = {
      ...group,
      groupId: groupId,
      _id: group._id || group.id // Keep _id for debugging
    };
    //console.log('mapGroupData output:', mapped);
    return mapped;
  }

  // 문제 목록 조회
  async fetchQuestionGroups(params = {}) {
    try {
      // 필터 파라미터 구성
      const requestParams = {
        page: params.page || 1,
        limit: params.limit || 20,
        ...params.filters
      };

      // myQuestions 파라미터 추가
      if (params.myQuestions !== undefined) {
        requestParams.myQuestions = params.myQuestions;
      }

      const response = await this.makeRequest({
        method: 'get',
        url: '/questions/groups/',
        api_url: CONF.ACHIEVE_API_URL,
        params: requestParams
      });

      if (response.status === 200) {
        const data = response.data;
        // 배열로 바로 내려오는 응답 호환 처리
        if (Array.isArray(data)) {
          // MongoDB _id를 groupId로 매핑
          const mappedItems = data.map(item => this.mapGroupData(item));
          const length = mappedItems.length || 0;
          return { items: mappedItems, page: 1, limit: length, total: length };
        }

        // 표준/유사 키 매핑 처리
        const items = (data.items || data.results || []).map(item => this.mapGroupData(item));
        const page = (data.page ?? data.current_page) || 1;
        const limit = (data.limit ?? data.page_size) || (items?.length || 0);
        const total = (data.total ?? data.count) || (items?.length || 0);
        return { items, page, limit, total };
      }
    } catch (error) {
      console.error('Error fetching question groups:', error)
      throw error
    }
  }

  // 특정 문제 조회
  async fetchQuestionGroup(groupId) {
    try {
      const response = await this.makeRequest({
        method: 'get',
        url: `/questions/groups/${groupId}`,
        api_url: CONF.ACHIEVE_API_URL
      });

      if (response.status === 200) {
        return this.mapGroupData(response.data)
      }
    } catch (error) {
      console.error('Error fetching question group:', error)
      throw error
    }
  }

  // 문제 생성
  async createQuestionGroup(groupData) {
    try {
      // groupId를 제거하고 서버로 전송 (MongoDB는 _id를 자동 생성)
      // eslint-disable-next-line no-unused-vars
      const { groupId, ...dataToSend } = groupData;
      
      console.log('Creating question group with data:', dataToSend)
      console.log('Questions with images:', dataToSend.questions?.map(q => ({
        questionId: q.questionId,
        questionText: q.questionText?.substring(0, 50) + '...',
        choices: q.choices?.map(c => ({
          choiceText: c.choiceText,
          hasImage: !!c.image?.file
        }))
      })))
      
      const response = await this.makeRequest({
        method: 'post',
        url: '/questions/groups/',
        api_url: CONF.ACHIEVE_API_URL,
        data: dataToSend
      });

      if (response.status === 201) {
        console.log('Server response data:', response.data)
        
        // 서버가 ID만 반환하는 경우 처리
        if (typeof response.data === 'string') {
          // ID와 원본 데이터를 조합해서 완전한 객체 생성
          const createdGroup = {
            ...dataToSend,
            _id: response.data,
            groupId: response.data
          }
          console.log('Created group object:', createdGroup)
          return createdGroup
        } else {
          // 전체 객체가 반환된 경우
          return this.mapGroupData(response.data)
        }
      }
    } catch (error) {
      console.error('Error creating question group:', error)
      throw error
    }
  }

  // 문제 수정
  async updateQuestionGroup(groupId, groupData) {
    try {
      // groupId를 제거하고 _id는 URL 파라미터로만 사용
      // eslint-disable-next-line no-unused-vars
      const { groupId: _, ...dataToSend } = groupData;
      
      console.log('Updating question group with data:', dataToSend)
      console.log('Questions with images:', dataToSend.questions?.map(q => ({
        questionId: q.questionId,
        questionText: q.questionText?.substring(0, 50) + '...',
        choices: q.choices?.map(c => ({
          choiceText: c.choiceText,
          hasImage: !!c.image?.file
        }))
      })))
      
      const response = await this.makeRequest({
        method: 'put',
        url: `/questions/groups/${groupId}`,
        api_url: CONF.ACHIEVE_API_URL,
        data: dataToSend
      });

      console.log('Server update dataToSend:', dataToSend)
      if (response.status === 200) {
        //console.log('Server update response data:', response.data)
        return this.mapGroupData(response.data)
      }
    } catch (error) {
      console.error('Error updating question group:', error)
      throw error
    }
  }

  // 문제 삭제
  async deleteQuestionGroup(groupId) {
    try {
      const response = await this.makeRequest({
        method: 'delete',
        url: `/questions/groups/${groupId}`,
        api_url: CONF.ACHIEVE_API_URL
      });

      if (response.status === 204 || response.status === 200) {
        return true
      }
    } catch (error) {
      console.error('Error deleting question group:', error)
      throw error
    }
  }

  // 문제은행 통계 조회
  async fetchStatistics() {
    try {
      const response = await this.makeRequest({
        method: 'get',
        url: '/questions/statistics',
        api_url: CONF.ACHIEVE_API_URL
      });

      if (response.status === 200) {
        const data = response.data;
        // 백엔드 snake_case → 프론트엔드 camelCase 변환
        return {
          totalGroups: data.total_groups || 0,
          totalQuestions: data.total_questions || 0,
          verifiedCount: data.verified_questions || 0,
          unverifiedCount: data.unverified_questions || 0
        };
      }
    } catch (error) {
      console.error('Error fetching statistics:', error);
      throw error;
    }
  }

  // 문제 검색
  async searchQuestions(searchParams) {
    try {
      const response = await this.makeRequest({
        method: 'post',
        url: '/questions/search/',
        api_url: CONF.ACHIEVE_API_URL,
        data: searchParams
      });

      if (response.status === 200) {
        const payload = response.data
        const rawItems = Array.isArray(payload)
          ? payload
          : (payload.items || payload.results || payload.data || [])

        const mapLevel = (level) => {
          const l = (level || '').toString().toLowerCase()
          // direct mappings
          if (['low', 'middle', 'high'].includes(l)) return l
          // CEFR to 3-bucket mapping
          if (l === 'a1' || l === 'a2') return 'low'
          if (l === 'b1' || l === 'b2') return 'middle'
          if (l === 'c1' || l === 'c2') return 'high'
          return 'middle'
        }

        let items = rawItems.map(q => ({
          questionId: q.questionId || q.id || q._id,
          questionNumber: q.questionNumber || q.unit || 0,
          level: mapLevel(q.level),
          skill: (q.skill || q.category || '').toString().toLowerCase(),
          type: q.type || (Array.isArray(q.choices) && q.choices.length > 0 ? 'MULTIPLE_CHOICE' : 'SHORT_ANSWER'),
          content: q.content || q.questionText || '',
          questionText: q.questionText || q.content || '',
          choices: (q.choices || []).map(c => ({
            text: c.choiceText || c.text || '',
            choiceText: c.choiceText || c.text || '',
            isAnswer: !!(c.isAnswer || c.correct),
            correct: !!(c.isAnswer || c.correct),
            // 선택지 이미지 정보 포함
            image: c.image || null
          })),
          textBook: q.textBook || q.book || '',
          unit: q.unit || '',
          timeLimit: q.timeLimit || 0,
          score: q.score || 0,
          // 그룹 정보 필드 추가
          questionGroupId: q.questionGroupId || null,
          instruction: q.instruction || '',
          example: q.example || '',
          readingScript: q.readingScript || '',
          groupAudio: q.groupAudio || null,
          // 그룹 동영상 URL 목록 — 2026-10-12 부터 검색 응답이 문항마다 복사(항상 배열). 예전 응답·null 도 [] 로
          groupVideos: normalizeGroupVideos(q.groupVideos),
          // 문제별 오디오 정보 (전체 객체)
          questionAudio: q.questionAudio || null,
          audioFile: q.questionAudio && q.questionAudio.file
            ? { file: q.questionAudio.file, script: q.questionAudio.script || '' }
            : null
        }))

        // 클라이언트 사이드 단원 필터링 (백엔드 미지원 대비)
        if (searchParams.textbookUnits && Object.keys(searchParams.textbookUnits).length > 0) {
          const unitMap = searchParams.textbookUnits
          items = items.filter(q => {
            if (!q.unit) return true
            for (const [barcode, units] of Object.entries(unitMap)) {
              if (q.textBook === barcode || (q.textBook && q.textBook.includes(barcode))) {
                return units.includes(Number(q.unit))
              }
            }
            return true
          })
        }

        return { items }
      }
    } catch (error) {
      console.error('Error searching questions:', error)
      throw error
    }
  }
}

// 단일 인스턴스 export
export default new QuestionBankService()